import { POIFormModal } from '../components/POIFormModal';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDemoStore, type POI } from '../store/demoStore';
import { POIDetailModal } from '../components/POIDetailModal';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Users, Shield, AlertCircle, Activity, Plus, Trash2, Edit2, Store, Settings, X, Car, MapPin } from 'lucide-react';
import Header from '../components/layout/Header';
import { useAlertSound } from '../hooks/useAlertSound';
import { useNavigate } from 'react-router-dom';
import { api, type StoreData, type AlertFromAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import StoreSelectorModal from '../components/StoreSelectorModal';
import { PoiListManager } from '../components/PoiListManager';
import { AuthorizedPersonsManager, AuthorizedPlatesManager } from '../components/AuthorizedManagers';
import TrainingSimulation from '../components/TrainingSimulation';
import ReactDOM from 'react-dom';
import { cn, isValidImageUrl } from '@/lib/utils';
import { t } from 'i18next';
import UserAddToStoresModal from '../components/UserAddToStoresModal';
import { useModal } from '../context/ModalContext';
import AdminFooter from '../components/layout/AdminFooter';
import { useConnectivity } from '../hooks/useConnectivity';
import OfflineShield from '../components/OfflineShield';
import AdminResetDialog from '../components/AdminResetDialog';
import { KeyRound } from 'lucide-react';

// Simple PortalToBody component
function PortalToBody({ children }: { children: React.ReactNode }) {
  return ReactDOM.createPortal(children, document.body);
}

// ---------- Alerts Log Modal ----------
function AlertsLogModal({ alerts, onClose, onPoiClick }: { alerts: AlertFromAPI[]; onClose: () => void; onPoiClick?: (alert: AlertFromAPI) => void }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background rounded-lg shadow-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{t('admin.alerts24h')}</h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-muted"><X className="w-5 h-5" /></button>
        </div>
        {alerts.length === 0 ? (
          <p className="text-muted-foreground">{t('admin.noAlerts')}</p>
        ) : (
          <div className="space-y-2">
            {alerts.map(alert => (
              <Card
                key={alert.id}
                className={`p-3 text-sm ${(alert.poi || alert.event_type === 'access_control') && onPoiClick ? 'cursor-pointer hover:shadow-md' : ''}`}
                onClick={() => {
                  if (alert.poi && onPoiClick) {
                    onPoiClick(alert);
                  } else if (alert.event_type === 'access_control' && onPoiClick) {
                    onPoiClick(alert);
                  }
                }}
              >
                <div className="flex justify-between">
                  <span className="font-medium">
                    {alert.poi ? alert.poi.code : 'Access Control'}
                  </span>
                  <span className={
                    alert.status === 'active' ? 'text-red-600' :
                    alert.status === 'acknowledged' ? 'text-blue-600' : 'text-green-600'
                  }>
                    {alert.status}
                  </span>
                </div>
                  <p className="text-xs text-muted-foreground">
                    {new Date(alert.createdAt).toLocaleString()} · {alert.poi ? t('guard.threat') + ': ' + alert.poi.threatLevel : 'Unauthorized'}
                  </p>
                  {alert.store_name && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" /> {alert.store_name}
                    </p>
                  )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function POIsFilterModal({
  pois,
  title,
  onClose,
  showNetworkBadge,
  currentClientId, 
  onPoiClick,
}: {
  pois: POI[];
  title: string;
  onClose: () => void;
  showNetworkBadge?: boolean;
  currentClientId?: string | null;
  onPoiClick?: (poi: POI) => void;
}) {
  const [search, setSearch] = useState('');
  const filtered = pois.filter(p => p.code.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div className="bg-background rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{title} ({filtered.length})</h2>
          <button onClick={onClose} className="p-1"><X className="w-5 h-5" /></button>
        </div>
        <Input
          placeholder="Search code..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-4"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map(poi => (
            <Card
              key={poi.id}
              className="p-3 flex gap-3 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => onPoiClick?.(poi)}
            >
              {isValidImageUrl(poi.photoUrl) ? (
                <img src={poi.photoUrl} className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center text-xs text-muted-foreground">
                  {t('admin.noImagesAvailable')}
                </div>
              )}
              <div>
                <span className="font-mono text-sm">{poi.code}</span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  poi.threatLevel === 'High' ? 'bg-red-100 text-red-800' :
                  poi.threatLevel === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-blue-100 text-blue-800'
                }`}>{poi.threatLevel}</span>
                {showNetworkBadge && (
                  <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                    Network
                    {/* Show store name only if same client */}
                    {(poi.storeClientId && currentClientId && poi.storeClientId === currentClientId) 
                      ? ` (${(poi as any).storeName ?? ''})` 
                      : ''}
                  </span>
                )}
              </div>
            </Card>
          ))}
          {filtered.length === 0 && <p className="text-sm text-muted-foreground">No matches.</p>}
        </div>
      </div>
    </div>
  );
}


// ---------- Main Component ----------
export default function AdminDashboard() {
  const { t, i18n } = useTranslation();
  const { isOnline } = useConnectivity();
  const { user, activeStoreId } = useAuth();
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(activeStoreId || null);
  const currentStoreId = selectedStoreId || activeStoreId || user?.storeId;
  const [activeStoreFull, setActiveStoreFull] = useState<StoreData | null>(null);
  const { triggerAlert, mockPOIs, activeAlerts, clearAllAlerts, isDemoMode } = useDemoStore();
  const [selectedPoi, setSelectedPoi] = useState<POI | null>(null);
  const [simulationPoi, setSimulationPoi] = useState<POI>(mockPOIs[0]);
  const navigate = useNavigate();
  const { playAlert } = useAlertSound();


  // Real POIs state
  const [pois, setPois] = useState<POI[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAuthorized, setSelectedAuthorized] = useState<any>(null);
  const [markedPois, setMarkedPois] = useState<POI[]>([]);

  // Modal states
  const [showPoiForm, setShowPoiForm] = useState(false);
  const [editingPoi, setEditingPoi] = useState<POI | null>(null);
  const [showOwnPoisModal, setShowOwnPoisModal] = useState(false);
  const [showNetworkPoisModal, setShowNetworkPoisModal] = useState(false);
  const [showAuthorizedModal, setShowAuthorizedModal] = useState(false); // to be deprecated
  const [refreshKey, setRefreshKey] = useState(0);
  const [showAuthorizedPersonsModal, setShowAuthorizedPersonsModal] = useState(false);
  const [showAuthorizedPlatesModal, setShowAuthorizedPlatesModal] = useState(false);
  const [editTrainingEnabled, setEditTrainingEnabled] = useState(false);
  const [userTrainingEnabled, setUserTrainingEnabled] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const { showAlert } = useModal();
  const { showConfirm } = useModal();
  const [poiSearch, setPoiSearch] = useState('');

  // Store management modal (for admin's own store)
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [storeUsers, setStoreUsers] = useState<any[]>([]);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userRole, setUserRole] = useState<'guard' | 'admin'>('guard');
  const [modalError, setModalError] = useState('');
  const [success, setSuccess] = useState('');
  const [addingUser, setAddingUser] = useState(false);  // v0.4.6: prevents double-submit
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState<'guard' | 'admin'>('guard');
  const [editGlobalRole, setEditGlobalRole] = useState<'guard' | 'admin' | 'cadmin' | 'sadmin'>('guard');
  const [storeInfo, setStoreInfo] = useState<{ country_code: string; city_code: string } | null>(null);
  const [storeFlags, setStoreFlags] = useState<{ guardian_enabled: boolean; access_control_enabled: boolean } | null>(null);
  const [showUserAddModal, setShowUserAddModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  // v0.4.6i.9 BUG FIX: `resetTarget` was referenced at the bottom of
  // the component (AdminResetDialog) but the state was never declared,
  // producing the runtime error:
  //   "Uncaught ReferenceError: resetTarget is not defined"
  // The state holds the user that the admin clicked 🔑 "Reset" on.
  // `null` means the dialog is closed.
  const [resetTarget, setResetTarget] = useState<{
    userId: string;
    userName: string;
    userEmail: string;
  } | null>(null);

  // Alerts log modal
  const [showAlertsLog, setShowAlertsLog] = useState(false);
  const [alertsLog, setAlertsLog] = useState<AlertFromAPI[]>([]);
  const [alertCount, setAlertCount] = useState(0);
  const [pastAlertsCount, setPastAlertsCount] = useState(0);
  const [authorizedCount, setAuthorizedCount] = useState(0);  // to be deprecated
  const [authorizedPersonsCount, setAuthorizedPersonsCount] = useState(0);
  const [authorizedPlatesCount, setAuthorizedPlatesCount] = useState(0);
  const [selectedAlertPoi, setSelectedAlertPoi] = useState<any>(null);
  const [selectedAccessAlert, setSelectedAccessAlert] = useState<AlertFromAPI | null>(null);

  // Client selector for sadmin
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

  // Store selector for admin
  const [showStoreSelector, setShowStoreSelector] = useState(false);
  const [adminStores, setAdminStores] = useState<StoreData[]>([]);
  const isAlertActive = activeAlerts.length > 0;
  const isDemoUser = user?.email === 'admin@isentinel360.com' || user?.email === 'guard@isentinel360.com';
  const activeStoreName = useMemo(() => {
    if (!currentStoreId) return null;
    if (user?.role === 'superadmin') {
      const store = adminStores.find(s => s.id === currentStoreId);
      return store ? store.name : null;
    }
    if (!user?.stores) return null;
    const store = user.stores.find(s => s.store_id === currentStoreId);
    return store ? store.name : null;
  }, [currentStoreId, user, adminStores]);

  // Auto-select first store for SUPERADMIN when client is selected
  useEffect(() => {
    if (user?.role === 'superadmin' && selectedClientId && adminStores.length > 0 && !selectedStoreId) {
      const firstStore = adminStores[0];
      setSelectedStoreId(firstStore.id);
      localStorage.setItem('activeStoreId', firstStore.id);
    }
  }, [user?.role, selectedClientId, adminStores, selectedStoreId]);

  // Fetch POIs
  useEffect(() => {
    if (user?.role === 'superadmin' && !selectedClientId) {
      setPois([]);
      setLoading(false);
      return;
    }
    if (!isDemoMode && currentStoreId) {
      setLoading(true);
      api.getPOIs(currentStoreId)  // ✅ Pass currentStoreId
        .then(data => {
          setPois(data);
          setError(null);
        })
        .catch(err => { console.error(err); setError(err.message); })
        .finally(() => setLoading(false));
    } else {
      setPois(mockPOIs);
      setLoading(false);
    }
  }, [isDemoMode, mockPOIs, currentStoreId, refreshKey, selectedStoreId, selectedClientId, user?.role]);

// When POIs are fetched, mark them as Own vs Network
  useEffect(() => {
    if (!pois.length || !currentStoreId) {
      setMarkedPois(pois);
      return;
    }
  
    // Get the current store's ID to determine Own vs Network
    // For SUPERADMIN, we need to know which store is selected
    api.getStores().then(stores => {
      const selectedStore = stores.find(s => s.id === currentStoreId);
      if (!selectedStore) {
        setMarkedPois(pois);
        return;
      }
      
      // Mark POIs: if the POI's storeId matches the selected store, it's "Own"
      const marked = pois.map(p => ({
        ...p,
        isNetwork: p.storeId !== currentStoreId,
      }));
      setMarkedPois(marked);
    }).catch(() => {
      setMarkedPois(pois);
    });
  }, [pois, currentStoreId]);

  // For uploading POIs and calc embedding code
  useEffect(() => {
  if (currentStoreId) {
    api.getStores().then(stores => {
      const found = stores.find(s => s.id === currentStoreId);
      setActiveStoreFull(found || null);
    }).catch(console.error);
    } else {
      setActiveStoreFull(null);
    }
  }, [currentStoreId]);

  const edgeIp = activeStoreFull?.edge_server_ip;
  const edgePort = activeStoreFull?.edge_server_port ?? 3002;

  // Ensure live mode for real accounts
  useEffect(() => {
    if (user && user.email !== 'admin@isentinel360.com' && user.email !== 'guard@isentinel360.com') {
      useDemoStore.setState({ isDemoMode: false });
    }
  }, [user]);

  // Keep store flags up‑to‑date
  useEffect(() => {
    if (!currentStoreId) {
      setStoreFlags(null);
      return;
    }
    const fetchFlags = () => {
      api.getStores()
        .then(stores => {
          const store = stores.find((s: any) => s.id === currentStoreId);
          if (store) {
            setStoreFlags({
              guardian_enabled: store.guardian_enabled,
              access_control_enabled: store.access_control_enabled,
            });
          } else {
            setStoreFlags(null);
          }
        })
        .catch(() => setStoreFlags(null));
    };
    fetchFlags();
    const interval = setInterval(fetchFlags, 15000);
    return () => clearInterval(interval);
  }, [currentStoreId, selectedStoreId]);

  // Fetch store users when modal opens
  useEffect(() => {
    if (showStoreModal && currentStoreId) {
      // Fetch users
      api.getStoreUsers(currentStoreId)
        .then(setStoreUsers)
        .catch(console.error);
      // Fetch store details for country/city
      api.getStores().then(stores => {
        const store = stores.find((s: any) => s.id === currentStoreId);
        if (store) {
          setStoreInfo({ country_code: store.country_code, city_code: store.city_code });
        }
      }).catch(console.error);
    }
  }, [currentStoreId, showStoreModal]);  

  // Fetch admin's stores for the selector
  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'sadmin' || user.role === 'superadmin')) {
      api.getStores()
        .then(data => {
          const stores = data.filter((s: any) => {
            if (user.role === 'superadmin') {
              return selectedClientId ? s.client_id === selectedClientId : false;
            }
            return s.client_id === user.clientId || user.stores?.some((us: any) => us.store_id === s.id);
          });
          setAdminStores(stores);
        })
        .catch(console.error);
    }
  }, [user, selectedClientId]);

  // Auto‑refresh alert count
  useEffect(() => {
    if (user?.role === 'superadmin' && !selectedClientId) {
      setAlertCount(0);
      return;
    }
    if (!isDemoMode && currentStoreId) {
      const fetchCount = () => {
        api.getActiveAlerts(currentStoreId).then(alerts => setAlertCount(alerts.length)).catch(console.error);
      };
      fetchCount();
      const interval = setInterval(fetchCount, 10000); // every 10 seconds
      return () => clearInterval(interval);
    }
  }, [isDemoMode, currentStoreId]);

  // Fetch completed alerts periodically
  useEffect(() => {
  if (user?.role === 'superadmin' && !selectedClientId) {
    setPastAlertsCount(0);
    return;
  }
  if (!isDemoMode && currentStoreId) {
    const fetchPast = () => {
      api.getCompletedAlerts(currentStoreId)
        .then(data => setPastAlertsCount(data.length))
        .catch(console.error);
    };
    fetchPast();
    const interval = setInterval(fetchPast, 30000);
    return () => clearInterval(interval);
  }
}, [isDemoMode, currentStoreId]);

  // Fetch authorized count for the active store
  useEffect(() => {
    if (user?.role === 'superadmin' && !selectedClientId) {
      setAuthorizedPersonsCount(0);
      setAuthorizedPlatesCount(0);
      return;
    }
    if (!currentStoreId) {
      setAuthorizedCount(0); // to be deprecated
      setAuthorizedPersonsCount(0);
      setAuthorizedPlatesCount(0);
      return;
    }
    const fetchAuthorizedCount = () => {
      Promise.all([
        api.getAuthorizedPersons(currentStoreId),
        api.getAuthorizedPlates(currentStoreId),
      ])
        .then(([persons, plates]) => {
          setAuthorizedPersonsCount(persons.length);
          setAuthorizedPlatesCount(plates.length);
        })
        .catch(() => {
          setAuthorizedPersonsCount(0);
          setAuthorizedPlatesCount(0);
        });
    };
    fetchAuthorizedCount();
    const interval = setInterval(fetchAuthorizedCount, 30000);
    return () => clearInterval(interval);
  }, [currentStoreId, selectedStoreId]);

  // Keep selectedPoi in sync with the real data after edits
  useEffect(() => {
    if (selectedPoi && pois.length > 0) {
      const updated = pois.find(p => p.id === selectedPoi.id);
      if (updated && JSON.stringify(updated) !== JSON.stringify(selectedPoi)) {
        setSelectedPoi(updated);
      }
    }
  }, [pois, selectedPoi]);

  // Auto‑select the first store if none is selected
  useEffect(() => {
    if (user && user.stores && user.stores.length > 0 && !currentStoreId) {
      const firstStoreId = user.stores[0].store_id;
      setSelectedStoreId(firstStoreId);
      localStorage.setItem('activeStoreId', firstStoreId);
    }
  }, [user, currentStoreId]);

  // Fetch clients list for superadmin
  useEffect(() => {
    if (user?.role === 'superadmin' || user?.role === 'sadmin') {
      api.getClients().then(setClients).catch(console.error);
    }
  }, [user]);

  // Reset all data when the selected client changes
  useEffect(() => {
    setPois([]);
    setAlertCount(0);
    setPastAlertsCount(0);
    setAuthorizedPersonsCount(0);
    setAuthorizedPlatesCount(0);
    setAlertsLog([]);
    setError(null);
  }, [selectedClientId]);

  // Fetch alerts for the logs modal
  const fetchAlertsForLog = async () => {
      try {
        if (isDemoMode) {
          // In demo, use activeAlerts from store (they already have timestamps)
          setAlertsLog(activeAlerts.map(a => ({
            id: a.id,
            poi: a.poi ? {
              code: a.poi.code,
              threatLevel: a.poi.threatLevel
            } : null,
            createdAt: a.timestamp.toISOString(),
            status: (a as any).status || 'active',
          })) as any);
        } else {
        const data = await api.getActiveAlerts();
        setAlertsLog(data.map(a => ({
          ...a,
          createdAt: a.created_at,
          poi: a.poi ?? null,
        })));
        }
        setShowAlertsLog(true);
      } catch (err) {
        console.error(err);
      }
    };

  const refreshPois = async () => {
    if (!isDemoMode) {
      try {
        const data = await api.getPOIs();
        setPois(data);
      } catch (err) { console.error(err); }
    }
  };

  const handleEdit = (poi: POI) => {
    setEditingPoi(poi);
    setShowPoiForm(true);
  };

  const handleDelete = async (poiId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const ok = await showConfirm(t('admin.confirmDeletePOI') || 'Are you sure?');
    if (!ok) return;
    if (isDemoMode) {
      setPois(prev => prev.filter(p => p.id !== poiId));
    } else {
      try {
        await api.deletePOI(poiId);
        await refreshPois();
      } catch (err) { alert('Failed to delete POI'); }
    }
  };

    // Store user management
    const handleAddUser = async () => {
      if (!userEmail || !currentStoreId) return;
      if (addingUser) return;  // v0.4.6: ignore re-entry while in flight
      setAddingUser(true);
      setModalError('');
      try {
        await api.addStoreUser(currentStoreId, userEmail, userRole, userName, userRole === 'admin' ? userTrainingEnabled : false);
        setUserEmail(''); setUserName('');
        const users = await api.getStoreUsers(currentStoreId);
        setStoreUsers(users);
        setSuccess('User added');
        setTimeout(() => setSuccess(''), 2000);
      } catch (err: any) {
        setModalError(err.message);
      } finally {
        setAddingUser(false);
      }
    };

  const handleRemoveUser = async (storeId: string, userId: string) => {
    const ok = await showConfirm(t('admin.confirmRemoveUser') || 'Remove this user?');
    if (!ok) return;
    try {
      // ✅ Use the storeId parameter
      await api.removeStoreUser(storeId, userId);
      const users = await api.getStoreUsers(storeId);
      setStoreUsers(users);
      setSuccess('User removed');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err: any) { 
      setModalError(err.message); 
    }
  };

  const displayPois = isDemoMode ? mockPOIs : pois;

  // v0.4.6i.8 BUG FIX: the previous code did `setStoreFlags(null)` inside
  // `useMemo`, which is a React anti-pattern (useMemo must be pure). The
  // side effect has been moved to a dedicated `useEffect` below, and the
  // `defaultTab` value is now actually wired to the Tabs component so the
  // smart-tab logic works. `defaultTab` always returns a valid string
  // (never undefined) so Radix's Tabs doesn't bail.
  useEffect(() => {
    if (user?.role === 'superadmin' && !selectedClientId && storeFlags) {
      setStoreFlags(null);
    }
  }, [user?.role, selectedClientId, storeFlags]);

  const defaultTab = useMemo<string>(() => {
    if (!storeFlags) return 'users';                // still loading – keep users as fallback
    if (storeFlags.guardian_enabled) return 'pois';
    if (storeFlags.access_control_enabled) return 'authorized';
    return 'users';                                // no enabled features – default to users
  }, [storeFlags]);

  const handleDeletePoi = async (poiId: string) => {
    if (!confirm(t('admin.confirmDeletePOI') || 'Are you sure?')) return;
    if (isDemoMode) {
      setPois(prev => prev.filter(p => p.id !== poiId));
    } else {
      try {
        await api.deletePOI(poiId);
        await refreshPois();
      } catch (err) {
        alert('Failed to delete POI');
      }
    }
  };

    return (
      <div className="h-dvh flex flex-col bg-background">
        <div className="flex-shrink-0">
          <Header view="admin" />
        </div>
        {!isOnline ? <OfflineShield /> : (<>

        {user && (user.role === 'admin' || user.role === 'superadmin' || user.role === 'sadmin' || user.role === 'cadmin') && (
          <div className="max-w-7xl mx-auto px-6 pt-4 space-y-4">
            {/* Row 1: Client selector (superadmin only) */}
            {(user.role === 'superadmin' || user.role === 'sadmin') && (
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium">{t('admin.selectClient')}:</label>
                <select
                  value={selectedClientId || ''}
                  onChange={e => {
                    const clientId = e.target.value || null;
                    setSelectedClientId(clientId);
                    setSelectedStoreId(null);
                    setPois([]);
                    setAlertCount(0);
                    setPastAlertsCount(0);
                    setAuthorizedPersonsCount(0);
                    setAuthorizedPlatesCount(0);
                  }}
                  className="p-2 border rounded-md bg-background text-sm"
                >
                  <option value="">-- {t('admin.chooseClient')} --</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Row 2: Store selector for non‑superadmins */}
            {user.role !== 'superadmin' && user.stores && user.stores.length > 0 && (
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium">{t('admin.selectStore')}:</label>
                <select
                  value={currentStoreId || ''}
                  onChange={(e) => {
                    const newStoreId = e.target.value;
                    setSelectedStoreId(newStoreId);
                    localStorage.setItem('activeStoreId', newStoreId);
                    setRefreshKey(k => k + 1);
                  }}
                  className="p-2 border rounded-md bg-background text-sm"
                >
                  {user.stores.map(s => (
                    <option key={s.store_id} value={s.store_id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Row 3: Store selector for superadmin (visible only when a client is selected) */}
            {user.role === 'superadmin' && selectedClientId && adminStores.length > 0 && (
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium">{t('admin.selectStore')}:</label>
                <select
                  value={currentStoreId || ''}
                  onChange={(e) => {
                    const newStoreId = e.target.value;
                    setSelectedStoreId(newStoreId);
                    localStorage.setItem('activeStoreId', newStoreId);
                    setRefreshKey(k => k + 1);
                  }}
                  className="p-2 border rounded-md bg-background text-sm"
                >
                  {adminStores.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto p-6">

        {/* ────── Stats Cards ────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {/* System Status */}
          <Card className="p-4">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium">{t('admin.systemStatus')}</span>
            </div>
            <p className="text-sm font-medium mt-2 text-green-600">{t('admin.operational')}</p>
          </Card>

          {/* Active Stores */}
          <Card className="p-4">
            <div className="flex items-center gap-2">
              <Store className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium">{t('admin.activeStore')}</span>
            </div>
            <p className="text-lg font-semibold mt-2 truncate">
              {activeStoreName ||
                (user?.role === 'superadmin' && !selectedClientId
                  ? t('admin.selectClientPrompt')
                  : t('common.none'))}
            </p>
          </Card>

          {/* Total POIs – Own (clickable filter) */}
          <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => setShowOwnPoisModal(true)}>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-muted-foreground" />
              <span className="text-sm font-medium">{t('admin.totalPOIsOwn')}</span>
            </div>
            <p className="text-2xl font-bold mt-2">
              {displayPois.filter(p => !p.isNetwork).length}
            </p>
          </Card>

          {/* Total POIs – Network (clickable filter) */}
          <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => setShowNetworkPoisModal(true)}>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-500" />
              <span className="text-sm font-medium">{t('admin.totalPOIsNetwork')}</span>
            </div>
            <p className="text-2xl font-bold mt-2">
              {displayPois.filter(p => p.isNetwork).length}
            </p>
          </Card>

          {/* Alerts On‑Going (active alerts) */}
          <Card className="p-4 cursor-pointer hover:shadow-md" onClick={fetchAlertsForLog}>
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              <span className="text-sm font-medium">{t('admin.alertsOngoing')}</span>
            </div>
            <p className="text-2xl font-bold mt-2">{alertCount || (isDemoMode ? 0 : '...')}</p>
          </Card>

          {/* Past Alerts (24h) – placeholder, will open same log for now */}
          <Card className="p-4 cursor-pointer hover:shadow-md" onClick={async () => {
            const data = await api.getCompletedAlerts(currentStoreId);
            setAlertsLog(data);
            setShowAlertsLog(true);
          }}>
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium">{t('admin.pastAlerts24h')}</span>
            </div>
            <p className="text-2xl font-bold mt-2">{pastAlertsCount}</p>
          </Card>

          {/* Authorized Persons – clickable */}
          <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => setShowAuthorizedPersonsModal(true)}>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium">{t('admin.authorizedPersons')}</span>
            </div>
            <p className="text-2xl font-bold mt-2">{authorizedPersonsCount}</p>
          </Card>

          {/* Authorized Vehicles (License Plates) – clickable */}
          <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => setShowAuthorizedPlatesModal(true)}>
            <div className="flex items-center gap-2">
              <Car className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium">{t('admin.authorizedPlates')}</span>
            </div>
            <p className="text-2xl font-bold mt-2">{authorizedPlatesCount}</p>
          </Card>

        </div>

          {currentStoreId && user?.trainingEnabled && (
            <TrainingSimulation
              availableStores={[{ id: currentStoreId, name: user?.stores?.find(s => s.store_id === currentStoreId)?.name || 'Current Store' }]}
              storeId={currentStoreId}
              showAlways={true}
              onAlertCreated={() => {}}
            />
          )}

          {/* Store Management Button */}
          {currentStoreId && (
            <div className="mb-4 flex justify-center items-center">
              <Button variant="outline" className="text-lg px-6 py-3" onClick={() => setShowStoreModal(true)}>
                <Settings className="w-4 h-4 mr-2" /> {t('admin.manageStore')}
              </Button>
            </div>
          )}

        </div>

        {/* Store Management Modal */}
        <Dialog open={showStoreModal} onOpenChange={setShowStoreModal}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:w-[calc(100vw-4rem)] md:max-w-2xl lg:max-w-5xl max-h-[90vh] sm:max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-semibold text-lg">{t('admin.storeManagement')}</DialogTitle>
          </DialogHeader>
          {modalError && <div className="p-2 mb-2 bg-red-100 text-red-800 rounded text-sm">{modalError}</div>}
          {success && <div className="p-2 mb-2 bg-green-100 text-green-800 rounded text-sm">{success}</div>}

          <Tabs defaultValue={defaultTab} className="space-y-4">
            <TabsList className="overflow-x-auto">
              <TabsTrigger value="users">{t('admin.users')}</TabsTrigger>
              {storeFlags?.guardian_enabled && (
                <TabsTrigger value="pois">{t('admin.poiDatabase')}</TabsTrigger>
              )}
              {storeFlags?.access_control_enabled && (
                <TabsTrigger value="authorized">{t('admin.accessAuthorize')}</TabsTrigger>
              )}
            </TabsList>

            {/* Users Tab */}
            <TabsContent value="users" className="space-y-4">
              <h3 className="font-semibold text-lg mb-4">{t('admin.addRemoveExisting')}</h3>
              <div className="flex gap-3 mb-4">
                <Button variant="outline" size="sm" onClick={() => setShowUserAddModal(true)}>
                  <Plus className="w-4 h-4 mr-1" /> {t('admin.addGuardToStores')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowRemoveModal(true)}>
                  <X className="w-4 h-4 mr-1" /> {t('admin.removeFromStores')}
                </Button>
              </div>
              
              <h3 className="font-semibold text-lg">{t('admin.addNewGuards')}</h3>
              <div className="space-y-3">
                {/* Sacrificial hidden inputs to absorb autofill */}
                <input type="text" name="fake-usern" style={{ display: 'none' }} aria-hidden="true" />
                <input type="password" name="fake-passw" style={{ display: 'none' }} aria-hidden="true" />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.poiName')} <span className="text-red-500">*</span></label>
                  <Input
                    placeholder={t('flag.fullName')}
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    className="w-full"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('login.emailPlaceholder')}</label>
                  <Input
                    placeholder={t('admin.userEmail')}
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    className="w-full"
                    autoComplete="off"
                  />
                </div>
                <select value={userRole} onChange={(e) => setUserRole(e.target.value as any)} className="w-full p-2 border border-gray-300 rounded">
                  <option value="guard">Guard</option>
                </select>
                <Button onClick={handleAddUser} disabled={addingUser}>
                  {addingUser ? (t('common.loading') || 'Loading…') : t('common.add')}
                </Button>
              </div>
              
              {/* User list with edit/delete */}
              <Input
                placeholder={t('admin.searchUsers')}
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="mt-2 mb-2"
              />
              
            <div className="border rounded divide-y">
              {storeUsers.length === 0 && <p className="p-3 text-muted-foreground text-sm">No users yet</p>}
              {storeUsers
                .filter(u =>
                  (u.name || '').toLowerCase().includes(userSearch.toLowerCase()) ||
                  (u.email || '').toLowerCase().includes(userSearch.toLowerCase())
                )
                .map(u => {
                  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                  // 🔒 Determine if current user can edit/delete this user
                  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                  const isSuperadmin = user?.role === 'superadmin';
                  const isSadmin = user?.role === 'sadmin';
                  const isTargetSadmin = u.role === 'sadmin';
                  const isTargetSuperadmin = u.role === 'superadmin';
                  const isTargetPrimary = u.is_primary_sadmin || false;
                  const isSelf = u.id === user?.id;

                  // 🔒 SADMIN can only edit/delete Guards, Admins, Cadmins (NOT SADMIN, NOT SUPERADMIN, NOT self)
                  const canEdit = isSuperadmin || 
                    (isSadmin && !isTargetSadmin && !isTargetSuperadmin && !isTargetPrimary && !isSelf);

                  const canDelete = isSuperadmin || 
                    (isSadmin && !isTargetSadmin && !isTargetSuperadmin && !isTargetPrimary && !isSelf);

                  return (
                    // ✅ Fix 1: Key includes editingUserId to force re-render
                    <div key={`user-${u.id}-${editingUserId}`} className="flex items-center justify-between p-3">
                      {editingUserId === u.id ? (
                        // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                        // 📝 EDIT FORM
                        // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                        <div className="flex-1 space-y-2">
                          <Input
                            placeholder={t('login.fullNamePlaceholder')}
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                          />
                          <Input
                            placeholder={t('login.emailPlaceholder')}
                            value={editEmail}
                            onChange={(e) => setEditEmail(e.target.value)}
                          />
                          
                          {/* Store Role */}
                          <div>
                            <label className="text-xs text-muted-foreground">Store Role</label>
                            <select
                              value={editRole}
                              onChange={(e) => setEditRole(e.target.value as 'guard' | 'admin')}
                              className="w-full p-2 border rounded mt-1"
                            >
                              <option value="guard">Guard</option>
                              {(isSuperadmin || isSadmin) && <option value="admin">Admin</option>}
                            </select>
                          </div>

                          {/* Global Role - Only show for SADMIN/SUPERADMIN */}
                          {(isSuperadmin || isSadmin) && (
                            <div>
                              <label className="text-xs text-muted-foreground">Global Role</label>
                              <select
                                value={editGlobalRole}
                                onChange={(e) => {
                                  const newGlobalRole = e.target.value as 'guard' | 'admin' | 'cadmin' | 'sadmin';
                                  setEditGlobalRole(newGlobalRole);
                                  if (newGlobalRole === 'sadmin' || newGlobalRole === 'cadmin') {
                                    setEditRole('admin');
                                  } else if (newGlobalRole === 'guard') {
                                    setEditRole('guard');
                                  } else if (newGlobalRole === 'admin') {
                                    setEditRole('admin');
                                  }
                                }}
                                className="w-full p-2 border rounded mt-1"
                              >
                                <option value="guard">Guard</option>
                                <option value="admin">Admin</option>
                                <option value="cadmin">Chief Admin</option>
                                {isSuperadmin && (
                                  <option value="sadmin">SAdmin</option>
                                )}
                              </select>
                              <p className="text-[10px] text-muted-foreground mt-1">
                                {isSuperadmin
                                  ? 'You can assign SAdmin role.'
                                  : 'Only SUPERADMIN can assign SAdmin role.'}
                              </p>
                            </div>
                          )}

                          {/* Training toggle - only show for admin role */}
                          {editRole === 'admin' && (
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id="training_enabled_edit"
                                checked={editTrainingEnabled}
                                onChange={(e) => setEditTrainingEnabled(e.target.checked)}
                              />
                              <label htmlFor="training_enabled_edit" className="text-sm">Training Module</label>
                            </div>
                          )}

                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={async () => {
                                try {
                                  // 🔒 Prevent SADMIN from modifying SUPERADMIN or SADMIN
                                  if ((u.role === 'superadmin' || u.role === 'sadmin') && !isSuperadmin) {
                                    alert('You cannot modify Super Admin or SAdmin users.');
                                    return;
                                  }

                                  // ✅ Fix: Ensure storeRole is always 'guard' | 'admin'
                                  let storeRole: 'guard' | 'admin' = editRole;
                                  if (editGlobalRole === 'sadmin' || editGlobalRole === 'cadmin') {
                                    storeRole = 'admin';
                                  }

                                  if (editGlobalRole === 'sadmin' || editGlobalRole === 'cadmin') {
                                    storeRole = 'admin';
                                  }

                                  const globalRole = isSuperadmin ? editGlobalRole : undefined;

                                  await api.updateStoreUser(currentStoreId!, u.id, {
                                    name: editName,
                                    email: editEmail,
                                    role_in_store: storeRole,
                                    role: globalRole,
                                    training_enabled: editTrainingEnabled,
                                  });
                                  
                                  setEditingUserId(null);
                                  // Refresh user list
                                  const users = await api.getStoreUsers(currentStoreId!);
                                  setStoreUsers(users);
                                  
                                  // Clear edit form
                                  setEditName('');
                                  setEditEmail('');
                                  setEditRole('guard');
                                  setEditGlobalRole('guard');
                                  setEditTrainingEnabled(false);
                                } catch (err: any) {
                                  setModalError(err.message);
                                }
                              }}
                            >
                              {t('common.save')}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => {
                              setEditingUserId(null);
                              setEditName('');
                              setEditEmail('');
                              setEditRole('guard');
                              setEditGlobalRole('guard');
                              setEditTrainingEnabled(false);
                            }}>
                              {t('common.cancel')}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                        // 👤 USER DISPLAY (when not editing)
                        // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                        <>
                          <div>
                            <p className="font-medium">
                              {u.name}
                              {u.is_primary_sadmin && (
                                <span className="ml-2 text-xs text-amber-600 font-semibold">🔒 Primary</span>
                              )}
                              {u.role === 'superadmin' && (
                                <span className="ml-2 text-xs text-red-600 font-semibold">🔒 Super Admin</span>
                              )}
                              {u.role === 'sadmin' && (
                                <span className="ml-2 text-xs text-purple-600 font-semibold">🔒 SAdmin</span>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {u.email} · {u.role_in_store}
                              {u.role !== u.role_in_store && (
                                <span className="ml-1 font-medium text-blue-600 dark:text-blue-400">
                                  (Global: {u.role})
                                </span>
                              )}
                            </p>
                          </div>

                          {/* 🔒 Only show actions if user can edit/delete */}
                          {(canEdit || canDelete) && u.id !== user?.id && (
                            <div className="flex flex-col gap-2">
                              <div className="flex gap-2">
                                {canEdit && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      const currentUser = storeUsers.find(user => user.id === u.id);
                                      if (!currentUser) return;
                                      
                                      setEditingUserId(currentUser.id);
                                      setEditName(currentUser.name || '');
                                      setEditEmail(currentUser.email || '');
                                      setEditRole(currentUser.role_in_store as 'guard' | 'admin');
                                      setEditGlobalRole(currentUser.role as 'guard' | 'admin' | 'cadmin' | 'sadmin');
                                      setEditTrainingEnabled(currentUser.training_enabled || false);
                                    }}
                                  >
                                    {t('common.edit')}
                                  </Button>
                                )}
                                
                                {canDelete && (
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => {
                                      if (u.is_primary_sadmin) {
                                        alert('The primary SADMIN cannot be deleted.');
                                        return;
                                      }
                                      if (u.role === 'superadmin') {
                                        alert('Super Admin users cannot be deleted.');
                                        return;
                                      }
                                      if (u.role === 'sadmin' && !isSuperadmin) {
                                        alert('SAdmin users cannot be deleted by SADMIN.');
                                        return;
                                      }
                                      // ✅ Fix 3: Pass both storeId and userId
                                      handleRemoveUser(currentStoreId!, u.id);
                                    }}
                                  >
                                    {t('common.remove')}
                                  </Button>
                                )}

                                {/* v0.4.6i.9: open the reset dialog (password / device).
                                    SADMIN+ can reset any non-self, non-primary user; SUPERADMIN
                                    can reset anyone. Same protection rules as Edit / Remove. */}
                                {(canEdit || canDelete) && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setResetTarget({
                                        userId: u.id,
                                        userName: u.name || '',
                                        userEmail: u.email || '',
                                      });
                                    }}
                                  >
                                    <KeyRound className="w-3 h-3 mr-1" /> {t('common.reset')}
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}

                          {/* 🔒 If user cannot edit/delete, show a lock icon */}
                          {!canEdit && !canDelete && u.id !== user?.id && (
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                              <span>🔒</span> Protected
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
            </div>
            </TabsContent>

            {/* POI Database Tab (clickable mini cards) */}
            {storeFlags?.guardian_enabled && (
              <TabsContent value="pois" className="space-y-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold text-lg">{t('admin.storePOIs')}</h3>
                  <Button onClick={(e) => { e.stopPropagation(); setEditingPoi(null); setShowPoiForm(true); }} size="sm">
                    <Plus className="w-4 h-4 mr-1" /> {t('admin.addPOI')}
                  </Button>
                </div>
                <div className="mb-3">
                  <Input
                    placeholder={t('admin.searchCode')}
                    value={poiSearch}
                    onChange={(e) => setPoiSearch(e.target.value)}
                    className="w-full"
                  />
                </div>
                {/* Compute own store POIs (exclude network) */}
                {(() => {
                  const ownStorePOIs = (isDemoMode
                    ? pois
                    : pois.filter(p => p.storeId === currentStoreId))
                    .filter(p => p.code.toLowerCase().includes(poiSearch.toLowerCase()));
                  return ownStorePOIs.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('admin.noPOIs')}</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {ownStorePOIs.map((poi) => (
                        <div
                          key={poi.id}
                          className="p-3 cursor-pointer hover:shadow-md transition-shadow relative group"
                          onClick={() => setSelectedPoi(poi)}
                        >
                          {/* Edit / Delete buttons (only for own store, not network) */}
                          {!poi.isNetwork && (
                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleEdit(poi); }}
                                className="p-1 bg-background/80 rounded-full hover:bg-primary/10"
                                title={t('common.edit')}
                              >
                                <Edit2 className="w-3 h-3" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeletePoi(poi.id); }}
                                className="p-1 bg-background/80 rounded-full hover:bg-destructive/10"
                                title={t('common.delete')}
                              >
                                <Trash2 className="w-3 h-3 text-destructive" />
                              </button>
                            </div>
                          )}
                          <div className="flex gap-3">
                            <div className="flex-shrink-0">
                            {isValidImageUrl(poi.photoUrl) ? (
                              <img src={poi.photoUrl} alt={poi.code} className="w-16 h-16 rounded-full object-cover" />
                            ) : (
                              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center text-xs text-muted-foreground">
                                {t('admin.noImagesAvailable')}
                              </div>
                            )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold font-mono text-sm truncate">{poi.code}</h4>
                              <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs ${
                                (poi.threatLevel || 'Low') === 'High' ? 'bg-red-100 text-red-800' :
                                (poi.threatLevel || 'Low') === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {t(`threat.${(poi.threatLevel || 'Low').toLowerCase()}`)}
                              </span>
                              {poi.isNetwork && (
                                <span className="ml-1 inline-block px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                                  {t('admin.networkPOI')}
                                </span>
                              )}
                            </div>
                          </div>
                          {(poi as any).vehicle?.driverPhotoUrl && (poi as any).vehicle.driverPhotoUrl.startsWith('http') && (
                            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground border-t pt-2">
                              <span className="font-medium">Driver:</span>
                              <img
                                src={(poi as any).vehicle.driverPhotoUrl}
                                className="w-6 h-6 rounded-full object-cover"
                                alt="Driver"
                                onError={(e) => (e.currentTarget.style.display = 'none')}
                              />
                              <span className="font-mono">{(poi as any).vehicle.driverCode}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}

              </TabsContent>
            )}

            {/* Authorized Access Tab (only if access control enabled) */}
            {storeFlags?.access_control_enabled && (
              <TabsContent value="authorized" className="space-y-4">
                <h3 className="font-semibold text-lg">{t('admin.authorizedPersons')}</h3>
                {currentStoreId && (
                  <AuthorizedPersonsManager
                    storeId={currentStoreId}
                    onItemClick={(item) => setSelectedAuthorized({ type: 'person', data: item })}
                    edgeIp={edgeIp}
                    edgePort={edgePort}
                  />
                )}
                <h3 className="font-semibold text-lg mt-4">Authorized License Plates</h3>
                {currentStoreId && (
                  <AuthorizedPlatesManager
                    storeId={currentStoreId}
                    onItemClick={(item) => setSelectedAuthorized({ type: 'plate', data: item })}
                    //edgeIp={edgeIp}
                    //edgePort={edgePort}
                  />
                )}
              </TabsContent>
            )}
          </Tabs>
        </DialogContent>
        </Dialog>

        {/* Store Selector Modal (for admin) */}
        {showStoreSelector && (
          <StoreSelectorModal />
        )}

        {/* Alerts Log Modal */}
        {showAlertsLog && (
          <AlertsLogModal
            alerts={alertsLog}
            onClose={() => setShowAlertsLog(false)}
            onPoiClick={(alert) => {
              if (alert.poi) {
                setSelectedAlertPoi({
                  ...alert.poi,
                  lastSeen: alert.createdAt,
                });
                setShowAlertsLog(false);
              } else if (alert.event_type === 'access_control') {
                setSelectedAccessAlert(alert);
                setShowAlertsLog(false);
              }
            }}
          />
        )}

        {/* ────── Own POIs Filter Modal ────── */}
        {showOwnPoisModal && (
          <POIsFilterModal
            pois={displayPois.filter(p => !p.isNetwork)}
            title={t('admin.totalPOIsOwn')}
            onClose={() => setShowOwnPoisModal(false)}
            onPoiClick={(poi) => setSelectedPoi(poi)}
          />
        )}

        {/* ────── Network POIs Filter Modal ────── */}
        {showNetworkPoisModal && (
          <POIsFilterModal
            pois={displayPois.filter(p => p.isNetwork)}
            title={t('admin.totalPOIsNetwork')}
            onClose={() => setShowNetworkPoisModal(false)}
            showNetworkBadge
            currentClientId={user?.clientId ?? null}
            onPoiClick={(poi) => setSelectedPoi(poi)}
          />
        )}

        {/* Authorized Persons Modal */}
        {showAuthorizedPersonsModal && currentStoreId && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
            <div className="bg-background rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">{t('admin.authorizedPersons')}</h2>
                <button onClick={() => setShowAuthorizedPersonsModal(false)} className="p-1"><X className="w-5 h-5" /></button>
              </div>
              <AuthorizedPersonsManager
                storeId={currentStoreId}
                onItemClick={(item) => {
                  setSelectedAuthorized({ type: 'person', data: item });
                }}
                edgeIp={edgeIp}
                edgePort={edgePort}
              />
            </div>
          </div>
        )}

        {/* Authorized Plates Modal */}
        {showAuthorizedPlatesModal && currentStoreId && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
            <div className="bg-background rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">{t('admin.authorizedPlates')}</h2>
                <button onClick={() => setShowAuthorizedPlatesModal(false)} className="p-1"><X className="w-5 h-5" /></button>
              </div>
              <AuthorizedPlatesManager
                storeId={currentStoreId}
                onItemClick={(item) => {
                  setSelectedAuthorized({ type: 'plate', data: item });
                }}
              />
            </div>
          </div>
        )}

        {/* Access control clickable from mini cards to large cards */}
        {selectedAuthorized && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
            <div className="bg-background rounded-lg p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">
                  {selectedAuthorized.type === 'person' ? 'Authorized Person' : 'Authorized License Plate'}
                </h2>
                <button onClick={() => setSelectedAuthorized(null)} className="p-1"><X className="w-5 h-5" /></button>
              </div>
              {selectedAuthorized.type === 'person' ? (
                <div>
                  <p><strong>{t('flag.fullName')}:</strong> {selectedAuthorized.data.name}</p>
                  {selectedAuthorized.data.photo_url && (
                    <img src={selectedAuthorized.data.photo_url} className="w-32 h-32 rounded-full object-cover my-2" />
                  )}
                  {selectedAuthorized.data.notes && <p><strong>{t('admin.notes')}:</strong> {selectedAuthorized.data.notes}</p>}
                </div>
              ) : (
                <div>
                  <p><strong>License Plate:</strong> {selectedAuthorized.data.license_plate}</p>
                  {selectedAuthorized.data.make && <p><strong>Make:</strong> {selectedAuthorized.data.make}</p>}
                  {selectedAuthorized.data.model && <p><strong>Model:</strong> {selectedAuthorized.data.model}</p>}
                  {selectedAuthorized.data.color && <p><strong>Color:</strong> {selectedAuthorized.data.color}</p>}
                  {selectedAuthorized.data.photo_url && (
                    <img src={selectedAuthorized.data.photo_url} className="w-full h-32 object-cover rounded my-2" />
                  )}
                  {selectedAuthorized.data.notes && <p><strong>{t('admin.notes')}:</strong> {selectedAuthorized.data.notes}</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* POI Form Modal – inside the dialog portal */}
        {showPoiForm && (
          <POIFormModal
            poi={editingPoi}
            onClose={() => setShowPoiForm(false)}
            onSuccess={() => {
              setShowPoiForm(false);
              refreshPois();
              setRefreshKey(k => k + 1);
            }}
            isDemoMode={isDemoMode}
            storeId={currentStoreId}
            edgeIp={edgeIp}
            edgePort={edgePort}
          />
        )}

        {/* POI Detail Modal – inside the dialog portal */}
        {selectedPoi && (
          <POIDetailModal
            poi={selectedPoi}
            onClose={() => setSelectedPoi(null)}
            onEdit={handleEdit}
            onDelete={async (poiId) => await handleDeletePoi(poiId)}
            readOnly={selectedPoi.isNetwork}
          />
        )}

        {/* POI Detail Modal from alert log */}
        {selectedAlertPoi && (
          <POIDetailModal
            poi={selectedAlertPoi}
            onClose={() => {
              setSelectedAlertPoi(null);
              setShowAlertsLog(true);    // ← reopen the alert list
            }}
            onEdit={handleEdit}
            onDelete={async (poiId) => await handleDeletePoi(poiId)}
            readOnly={true}
          />
        )}

        {/* Access Control alert detail */}
        {selectedAccessAlert && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
            <div className="bg-background rounded-lg shadow-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Unauthorised Access</h2>
                <button
                  onClick={() => {
                    setSelectedAccessAlert(null);
                    setShowAlertsLog(true);    // ← reopen the alert list
                  }}
                  className="p-1 rounded-full hover:bg-muted"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Capture image */}
              <div className="aspect-video bg-gray-200 dark:bg-gray-800 rounded-md overflow-hidden mb-4">
                {selectedAccessAlert.capture_image_url ? (
                  <img
                    src={selectedAccessAlert.capture_image_url}
                    alt="Unauthorised"
                    className="w-full h-full object-contain bg-muted"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                    No image available
                  </div>
                )}
              </div>

              {/* Details */}
              <div className="space-y-2 text-sm">
                <p>
                  <span className="font-medium">Time:</span>{' '}
                  {new Date(selectedAccessAlert.createdAt).toLocaleString()}
                </p>
                <p>
                  <span className="font-medium">Status:</span>{' '}
                  <span className={
                    selectedAccessAlert.status === 'active' ? 'text-red-600' :
                    selectedAccessAlert.status === 'acknowledged' ? 'text-blue-600' : 'text-green-600'
                  }>
                    {selectedAccessAlert.status}
                  </span>
                </p>
                {selectedAccessAlert.acknowledgedByName && (
                  <p>
                    <span className="font-medium">Acknowledged by:</span>{' '}
                    {selectedAccessAlert.acknowledgedByName}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Show User Add Modal */}
        {showUserAddModal && (
        <UserAddToStoresModal
          stores={user?.stores?.map(s => ({ id: s.store_id, name: s.name })) || []}
          role="guard"
          mode="add"
          onClose={() => setShowUserAddModal(false)}
          onSuccess={() => {
            setShowUserAddModal(false);
            setSuccess('Guard added to selected stores');
            setTimeout(() => setSuccess(''), 2000);
            if (currentStoreId) {
              api.getStoreUsers(currentStoreId).then(setStoreUsers).catch(console.error);
            }
          }}
        />
        )}

        {/* Set Show User Add Modal */}
        {showRemoveModal && (
          <UserAddToStoresModal
            stores={user?.stores?.map(s => ({ id: s.store_id, name: s.name })) || []}
            mode="remove"
            onClose={() => setShowRemoveModal(false)}
            onSuccess={() => {
              setShowRemoveModal(false);
              setSuccess('User removed from selected stores');
              setTimeout(() => setSuccess(''), 2000);
              if (currentStoreId) {
                api.getStoreUsers(currentStoreId).then(setStoreUsers).catch(console.error);
              }
            }}
          />
        )}
      </div>

      {/* v0.4.6i: reset-access dialog (NEW for AdminDashboard).
          Same component used in NetworkView and ClientDashboard. */}
      {resetTarget && (
        <AdminResetDialog
          open={!!resetTarget}
          onClose={() => setResetTarget(null)}
          userId={resetTarget.userId}
          userName={resetTarget.userName}
          userEmail={resetTarget.userEmail}
        />
      )}

</>)}
        <AdminFooter activeStoreName={activeStoreName} />
    </div>
   );
}
